/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useMemo } from "react";

import { selectIsTvDevice } from "../../../../viewModel/device";
import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../../viewModel";
import {
  selectIsActiveOptionVisible,
  selectActiveOptionFocusIndex,
  RecordingOption,
  selectActiveRecordingOption,
  setActiveOptionFocusIndex,
  tickActiveOptionFocus,
  commitPendingActiveOptionValue,
  closeOptionValue
} from "../../../../viewModel/myHub/seriesRecordingOptions";
import { useModalController } from "../../../hooks";
import ListSelectionDialog from "./ListSelectionDialog";
import { ListSelectionDialogController } from "./ListSelectionDialogController";
import { selectActiveOptionDescription } from "../../../../viewModel/myHub/seriesRecordingOptions/seriesRecordingOptionsSlice";

const ListSelectionDialogContainer: FunctionComponent =
  (): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
    const TAG: string = "ListSelectionDialogContainer";
    const dispatch = useUnityDispatch();

    const isVisible: boolean = useUnitySelector(selectIsActiveOptionVisible);
    const listFocusIndex: number = useUnitySelector(
      selectActiveOptionFocusIndex
    );
    const activeOption: RecordingOption | undefined = useUnitySelector(
      selectActiveRecordingOption
    );
    const description: string = useUnitySelector(selectActiveOptionDescription);

    const isVerticalPickerActive: boolean =
      isVisible === true && activeOption !== undefined;

    const modalController = useMemo(
      (): ListSelectionDialogController =>
        new ListSelectionDialogController(dispatch, UnityAppStore.getState),
      [dispatch]
    );

    useModalController(modalController, isVerticalPickerActive);

    const handleItemHover = useCallback(
      (index: number): void => {
        dispatch(setActiveOptionFocusIndex(index));
      },
      [dispatch]
    );

    /** Click selects the focused item and commits it immediately. */
    const handleItemClick = useCallback(
      (index: number): void => {
        dispatch(setActiveOptionFocusIndex(index));
        dispatch(tickActiveOptionFocus());
        dispatch(commitPendingActiveOptionValue());
      },
      [dispatch]
    );

    const handleCloseHover = useCallback((): void => {
      dispatch(setActiveOptionFocusIndex(-1));
    }, [dispatch]);

    const handleCancel = useCallback((): void => {
      dispatch(closeOptionValue());
    }, [dispatch]);

    if (isVerticalPickerActive !== true || activeOption === undefined) {
      return null;
    }

    const options: string[] =
      activeOption.horizontalOptions !== undefined &&
      activeOption.horizontalOptions.length > 0
        ? activeOption.horizontalOptions
        : [];

    if (options.length === 0) {
      return null;
    }

    const selectedIndex: number = activeOption.selectedIndex || 0;
    const showCloseButton: boolean = !selectIsTvDevice();

    return (
      <ListSelectionDialog
        title={activeOption.label}
        description={description}
        options={options}
        listFocusIndex={listFocusIndex}
        selectedIndex={selectedIndex}
        showCloseButton={showCloseButton}
        onItemHover={handleItemHover}
        onItemClick={handleItemClick}
        onCloseHover={handleCloseHover}
        onCancel={handleCancel}
      />
    );
  };

export default ListSelectionDialogContainer;
